import { type ViewChangeType } from '@databases/utils/database/view/view';
import { proxy } from 'valtio';

import {
  type ContextMenuPosition,
  type MergedActionsAPI,
  type SelectionsEffectCellContextMenuContext,
} from '../select-value-menu/types';

export type ActionsBuilderWithViewContext = (
  setViewChange: (view: ViewChangeType) => void
) => MergedActionsAPI;

export interface ContextMenuMenuData {
  context: SelectionsEffectCellContextMenuContext;
  actionsBuilder: ActionsBuilderWithViewContext;
}

export interface ContextMenuState {
  isVisible: boolean;
  position: ContextMenuPosition | null;
  version: number;
}

/**
 * Context menu state management using Valtio for reactive updates.
 *
 * This module provides a singleton state manager for context menus, tracking:
 * - Visibility state
 * - Screen position
 * - Menu context and actions builder (kept outside the proxy for performance)
 * - Version counter (incremented on show to trigger re-renders)
 *
 * The version field ensures React components re-render even when the menu
 * is shown at the same position with different items.
 *
 * Without version: isVisible stays true, position can be the same reference
 * Valtio may not emit a new snapshot -> no re-render -> getMenuData() isn't called again -> stale items.
 * With version: state.version += 1 always changes the proxy
 * -> new snapshot -> re-render -> correct items from getMenuData().
 */
function createContextMenuState() {
  const state = proxy<ContextMenuState>({
    isVisible: false,
    position: null,
    version: 0,
  });

  let currentMenuData: ContextMenuMenuData | null = null;

  function show(
    position: ContextMenuPosition,
    context: SelectionsEffectCellContextMenuContext,
    actionsBuilder: ActionsBuilderWithViewContext
  ) {
    currentMenuData = { context, actionsBuilder };

    state.isVisible = true;
    state.position = position;
    state.version += 1;
  }

  function hide() {
    state.isVisible = false;
    state.position = null;

    currentMenuData = null;
  }

  function getMenuData(): ContextMenuMenuData | null {
    return currentMenuData;
  }

  return {
    state,
    show,
    hide,
    getMenuData,
  };
}

export const contextMenuState = createContextMenuState();
